import { GitHubInstallationStatus, TicketingAcquisitionMethod, TicketingProvider } from '../graphql';
import { useCurrentCompany } from './useCurrentCompany';

/**
 * Reads the current company's GitHub App ticketing connection status (PLAT-386) and exposes the
 * company-query polling controls so the connect slide-over can poll PENDING -> ACTIVE after the
 * install redirect. Only a GITHUB_APP-acquired GitHub token carries an installationStatus; Merge-
 * GitHub (and other providers) return null.
 */
export const useGitHubAppInstallStatus = (): {
  installationStatus: GitHubInstallationStatus | null;
  /** The GitHub organization the connection is for, so a retry can reuse it without asking again. */
  organization: string | null;
  loading: boolean;
  /** Re-reads the connection from the server and resolves with its fresh installation status. */
  refreshInstallationStatus: () => Promise<GitHubInstallationStatus | null>;
  startPolling: (pollIntervalMs: number) => void;
  stopPolling: () => void;
} => {
  const { company, loading, refetch, startPolling, stopPolling } = useCurrentCompany();

  const findGitHubAppToken = (
    ticketingTokens: NonNullable<typeof company>['ticketingTokens'] | undefined,
  ): NonNullable<typeof company>['ticketingTokens'][number] | undefined =>
    ticketingTokens?.find(
      (token) =>
        token.ticketingProvider === TicketingProvider.GithubIssues &&
        token.acquisitionMethod === TicketingAcquisitionMethod.GithubApp,
    );
  const githubAppToken = findGitHubAppToken(company?.ticketingTokens);

  const refreshInstallationStatus = async (): Promise<GitHubInstallationStatus | null> => {
    const refreshed = await refetch();
    return findGitHubAppToken(refreshed?.ticketingTokens)?.installationStatus ?? null;
  };

  return {
    installationStatus: githubAppToken?.installationStatus ?? null,
    organization: githubAppToken?.organization ?? null,
    loading,
    refreshInstallationStatus,
    startPolling,
    stopPolling,
  };
};
